import React, { useState, useEffect, useRef, useCallback } from 'react';
import ensureMapsLoaded from './loadMaps';
import { rentApi } from './api';

const AreaInsightsPage = ({ initialLocation, onNavigate }) => {
    const [location, setLocation] = useState(initialLocation || { name: 'Delhi', lat: 28.6138, lng: 77.2090 });
    const [radiusKm, setRadiusKm] = useState(5);
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [googleReady, setGoogleReady] = useState(!!window.google?.maps);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const searchRef = useRef(null);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const searchTimeout = useRef(null);

    // Wait for Google Maps API to load
    useEffect(() => {
        ensureMapsLoaded().catch(() => {});
        const init = () => {
            if (window.google?.maps?.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                const d = document.createElement('div');
                placesService.current = new window.google.maps.places.PlacesService(d);
                setGoogleReady(true);
            }
        };
        if (window.google?.maps) init();
        else {
            const i = setInterval(() => {
                if (window.google?.maps) { init(); clearInterval(i); }
            }, 200);
            return () => clearInterval(i);
        }
    }, []);

    useEffect(() => {
        const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    // Fetch insights
    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            try {
                const data = await rentApi.getInsights({ lat: location.lat, lng: location.lng, radiusKm });
                setInsights(data);
            } catch (err) { console.error('Failed to load insights', err); setInsights(null); }
            finally { setLoading(false); }
        };
        fetch();
    }, [location.lat, location.lng, radiusKm]);

    // Format rent for pill labels: ₹10K, ₹1.2L etc.
    const formatPillPrice = useCallback((rent) => {
        if (!rent) return '₹?';
        if (rent >= 100000) return `₹${(rent / 100000).toFixed(1)}L`;
        if (rent >= 1000) return `₹${(rent / 1000).toFixed(rent % 1000 === 0 ? 0 : 1)}K`;
        return `₹${rent}`;
    }, []);

    // Build Leaflet HTML
    const buildMapHtml = useCallback(() => {
        const centerLat = location.lat;
        const centerLng = location.lng;

        const markers = (insights?.recentContributions || []).map(c => {
            const cLat = c.location?.latitude || centerLat;
            const cLng = c.location?.longitude || centerLng;
            const isListing = c.source === 'LISTING';
            const color = isListing ? '#3B82F6' : '#10B981';
            const priceLabel = formatPillPrice(c.rent);
            
            const popupContent = `<div style="padding:10px 14px;font-family:system-ui;text-align:center;">
                <strong style="font-size:16px;color:#111827;display:block;margin-bottom:4px;">${(c.location?.name || 'Property').replace(/["']/g, "")}</strong>
                <div style="font-size:18px;font-weight:800;color:#10B981;margin-bottom:4px;">₹${c.rent?.toLocaleString()}/mo</div>
                <span style="color:#6B7280;font-size:13px">${c.postType || 'Apartment'}${c.bhk ? ` &middot; ${c.bhk} BHK` : ''}${c.furnishing && c.furnishing !== 'ANY' ? ` &middot; ${c.furnishing}` : ''}</span><br/>
                <span style="display:inline-block;margin-top:6px;font-size:11px;padding:3px 10px;border-radius:8px;background:${isListing ? '#EFF6FF' : '#ECFDF5'};color:${isListing ? '#2563EB' : '#059669'};font-weight:700">${isListing ? 'From Listing' : 'Community'}</span>
            </div>`.replace(/\n/g, '');

            return `L.marker([${cLat}, ${cLng}], {
                icon: L.divIcon({
                    className: 'price-marker',
                    html: '<div style="background:${color};color:#fff;padding:4px 8px;border-radius:12px;font-size:11px;font-weight:800;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid #fff;display:inline-block;transform:translate(-50%,-50%);">${priceLabel}</div>',
                    iconSize: [0, 0],
                    iconAnchor: [0, 0]
                })
            }).bindPopup('${popupContent}').addTo(map);`;
        }).join('\n');

        return `<!DOCTYPE html>
<html><head>
<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>
  body { padding: 0; margin: 0; }
  html, body, #map { height: 100%; width: 100%; }
  .price-marker { background: transparent; border: none; }
</style>
</head><body>
<div id="map"></div>
<script>
  var map = L.map('map', { zoomControl: true }).setView([${centerLat}, ${centerLng}], 13);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap'
  }).addTo(map);

  ${markers}
  
  // Fit bounds to radius
  map.fitBounds(L.circle([${centerLat}, ${centerLng}], ${radiusKm * 1000}).getBounds());
</script>
</body></html>`;
    }, [insights, location, radiusKm, formatPillPrice]);

    const handleSearchInput = (e) => {
        const v = e.target.value;
        setSearchQuery(v);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!v.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
        setShowSuggestions(true);
        searchTimeout.current = setTimeout(() => {
            if (!autocompleteService.current) return;
            autocompleteService.current.getPlacePredictions(
                { input: v, componentRestrictions: { country: 'in' } },
                (preds, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) setSuggestions(preds);
                    else setSuggestions([]);
                }
            );
        }, 400);
    };

    const handleSelectSuggestion = (sugg) => {
        setSearchQuery(sugg.description);
        setShowSuggestions(false);
        if (!placesService.current) return;
        placesService.current.getDetails({ placeId: sugg.place_id, fields: ['geometry'] }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                setLocation({ name: sugg.structured_formatting?.main_text || sugg.description, lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
            }
        });
    };

    const BLUE = '#2563EB', BLUE_DARK = '#1E40AF';
    const formatRent = (v) => v ? `₹${Math.round(v).toLocaleString()}` : '—';

    return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6' }}>
            {/* Header */}
            <header style={{ background: `linear-gradient(155deg, ${BLUE_DARK} 0%, ${BLUE} 55%, #3B82F6 100%)`, padding: '100px 20px 48px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                <div style={{ maxWidth: '860px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', padding: '5px 16px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '12px' }}>
                        <span style={{ fontSize: '12px', color: 'white', fontWeight: '700' }}>📊 Community Data</span>
                    </div>
                    <h1 style={{ fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: '800', color: 'white', marginBottom: '8px', letterSpacing: '-0.5px' }}>
                        Rent Insights — <span style={{ color: '#F97316' }}>{location.name}</span>
                    </h1>
                    <p style={{ color: '#D9E6DC', fontSize: '15px', marginBottom: '24px' }}>Real rent data from listings & community contributions</p>

                    {/* Search */}
                    <div style={{ position: 'relative', maxWidth: '560px', zIndex: 300 }} ref={searchRef}>
                        <div style={{ display: 'flex', background: 'white', borderRadius: '14px', padding: '4px', boxShadow: '0 12px 40px rgba(0,0,0,0.15)' }}>
                            <input type="text" value={searchQuery} onChange={handleSearchInput} onFocus={() => { if (searchQuery) setShowSuggestions(true); }}
                                placeholder="Search locality..." style={{ flex: 1, border: 'none', outline: 'none', padding: '12px 16px', fontSize: '15px', fontWeight: '500', borderRadius: '10px', background: 'transparent' }} />
                            <select value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value))}
                                style={{ border: 'none', outline: 'none', padding: '8px 12px', fontSize: '13px', fontWeight: '700', color: BLUE, background: '#EFF6FF', borderRadius: '10px', cursor: 'pointer' }}>
                                <option value={2}>2 km</option><option value={5}>5 km</option><option value={10}>10 km</option><option value={20}>20 km</option>
                            </select>
                        </div>
                        {showSuggestions && suggestions.length > 0 && (
                            <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, background: 'white', borderRadius: '16px', boxShadow: '0 16px 48px rgba(0,0,0,0.2)', zIndex: 9999, padding: '6px', border: '1px solid #DDE3DC', maxHeight: '280px', overflowY: 'auto' }}>
                                {suggestions.map((s, i) => (
                                    <div key={i} onClick={() => handleSelectSuggestion(s)} style={{ padding: '10px 12px', cursor: 'pointer', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#374151' }}
                                        onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                                        {s.description}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 20px', position: 'relative', zIndex: 1 }}>

                {/* Stats Cards — show skeleton when loading */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', margin: '-24px 0 24px', position: 'relative', zIndex: 1 }}>
                    {[
                        { label: 'Average Rent', value: loading ? '...' : (insights?.averageRent ? formatRent(insights.averageRent) : '—'), icon: '📊', bg: 'linear-gradient(135deg, #EFF6FF, #E4EFC0)', color: BLUE },
                        { label: 'Min Rent', value: loading ? '...' : (insights?.minRent ? formatRent(insights.minRent) : '—'), icon: '⬇️', bg: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)', color: '#059669' },
                        { label: 'Max Rent', value: loading ? '...' : (insights?.maxRent ? formatRent(insights.maxRent) : '—'), icon: '⬆️', bg: 'linear-gradient(135deg, #FEF3C7, #FDE68A)', color: '#D97706' },
                        { label: 'Data Points', value: loading ? '...' : (insights?.totalDataPoints || 0), icon: '📍', bg: 'linear-gradient(135deg, #F3E8FF, #E9D5FF)', color: '#7C3AED' },
                    ].map((c, i) => (
                        <div key={i} style={{ background: 'white', borderRadius: '20px', padding: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', opacity: loading ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>{c.icon}</div>
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</span>
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: '800', color: c.color }}>{c.value}</div>
                        </div>
                    ))}
                </div>

                {/* Map — ALWAYS in DOM, never unmounted */}
                <div style={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: '1px solid #DDE3DC', marginBottom: '24px', position: 'relative' }}>
                    <div style={{ padding: '16px 20px', background: 'white', borderBottom: '1px solid #E5E9E3', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '800', fontSize: '16px', color: '#111827' }}>📍 Price Map</span>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', fontWeight: '700' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', background: BLUE, display: 'inline-block' }} /> Listings</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }} /> Community</span>
                        </div>
                    </div>
                    <iframe srcDoc={buildMapHtml()} style={{ width: '100%', height: '400px', border: 'none', background: '#DDE3DC' }} title="Price Map" />
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, backdropFilter: 'blur(2px)' }}>
                            <div style={{ background: 'white', padding: '16px 28px', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', fontWeight: '700', color: '#6B7280', fontSize: '14px' }}>
                                📊 Loading insights...
                            </div>
                        </div>
                    )}
                </div>

                {/* Breakdown — only when data exists */}
                {!loading && insights?.breakdown && insights.breakdown.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '24px', padding: '24px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#111827', marginBottom: '16px' }}>📋 Breakdown by Type</h3>
                        <div style={{ display: 'grid', gap: '12px' }}>
                            {insights.breakdown.map((b, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', background: '#F3F4F6', borderRadius: '14px', gap: '16px' }}>
                                    <span style={{ padding: '6px 14px', borderRadius: '10px', background: b.postType === 'PG' ? '#FEF3C7' : b.postType === 'ROOM' ? '#ECFDF5' : '#EFF6FF', color: b.postType === 'PG' ? '#D97706' : b.postType === 'ROOM' ? '#059669' : BLUE, fontWeight: '800', fontSize: '13px', minWidth: '60px', textAlign: 'center' }}>
                                        {b.postType}
                                    </span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '800', fontSize: '16px', color: '#111827' }}>{formatRent(b.averageRent)} <span style={{ fontSize: '12px', fontWeight: '500', color: '#9CA3AF' }}>avg</span></div>
                                        <div style={{ fontSize: '12px', color: '#6B7280' }}>{formatRent(b.minRent)} — {formatRent(b.maxRent)}</div>
                                    </div>
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#9CA3AF' }}>{b.dataPoints} pts</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Recent Contributions List */}
                {!loading && insights?.recentContributions && insights.recentContributions.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '24px', padding: '24px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#111827', marginBottom: '16px' }}>🕒 Recent Contributions</h3>
                        <div style={{ display: 'grid', gap: '12px' }}>
                            {insights.recentContributions.slice(0, 10).map((c, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', background: '#F3F4F6', borderRadius: '14px', gap: '16px', border: '1px solid #E2E8F0' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827', marginBottom: '2px' }}>
                                            {c.location?.name || 'Property'}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                            {c.postType || 'Apartment'} • {c.source === 'LISTING' ? 'From Listing' : 'Community'}
                                        </div>
                                    </div>
                                    <div style={{ fontWeight: '800', fontSize: '16px', color: '#10B981' }}>
                                        ₹{c.rent.toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Single CTA */}
                {!loading && (
                    <div style={{ background: `linear-gradient(135deg, ${BLUE_DARK}, #3B82F6)`, borderRadius: '24px', padding: '32px', textAlign: 'center', marginBottom: '40px', boxShadow: '0 12px 40px rgba(34,106,70,0.25)' }}>
                        {(!insights?.totalDataPoints || insights.totalDataPoints === 0) ? (
                            <>
                                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
                                <h3 style={{ fontSize: '22px', fontWeight: '800', color: 'white', marginBottom: '8px' }}>No rent data for this area yet</h3>
                                <p style={{ color: '#D9E6DC', fontSize: '15px', marginBottom: '20px' }}>Be the first to contribute! Help build transparent pricing for everyone.</p>
                            </>
                        ) : (
                            <>
                                <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', marginBottom: '8px' }}>Help Improve These Insights</h3>
                                <p style={{ color: '#D9E6DC', fontSize: '14px', marginBottom: '20px' }}>Share what you pay to help others in your area</p>
                            </>
                        )}
                        <button onClick={() => onNavigate && onNavigate('contributeRent')}
                            style={{ padding: '14px 32px', borderRadius: '14px', background: 'white', color: BLUE_DARK, border: 'none', fontWeight: '800', fontSize: '15px', cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
                            🤝 Contribute Rent Data
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AreaInsightsPage;
